import { DomToCanvasRenderer } from '../../helpers/DomToCanvasRenderer';
const SHADOW_DOM_MONKEY_PATCH = `
HTMLElement.prototype.attachShadow = function(_init) {
  var el = this;
  var tag = el.tagName.toLowerCase();
  var pending = [];
  function rewriteHost(node) {
    if (node instanceof HTMLStyleElement && node.textContent) {
      node.textContent = node.textContent
        .replace(/:host\\(([^)]+)\\)/g, tag + '$1')
        .replace(/:host/g, tag);
    }
  }
  queueMicrotask(function() {
    var realAppend = Node.prototype.appendChild.bind(el);
    for (var i = 0; i < pending.length; i++) realAppend(pending[i]);
    pending.length = 0;
  });
  var fakeRoot = {
    append: function() {
      for (var i = 0; i < arguments.length; i++) {
        rewriteHost(arguments[i]);
        pending.push(arguments[i]);
      }
    },
    appendChild: function(node) { rewriteHost(node); pending.push(node); return node; },
    removeChild: function(n) { return el.removeChild(n); },
    insertBefore: function(n, ref) { return el.insertBefore(n, ref); },
    replaceChild: function(n, old) { return el.replaceChild(n, old); },
    contains: function(n) { return el.contains(n); },
    querySelector: function(s) { return el.querySelector(s); },
    querySelectorAll: function(s) { return el.querySelectorAll(s); },
    getElementById: function(id) { return el.querySelector('#' + id); },
    get innerHTML() { return el.innerHTML; },
    set innerHTML(v) { el.innerHTML = v; },
    get children() { return el.children; },
    get childNodes() { return el.childNodes; },
    get firstChild() { return el.firstChild; },
    get lastChild() { return el.lastChild; },
    get textContent() { return el.textContent; },
    set textContent(v) { el.textContent = v; },
    get host() { return el; },
  };
  Object.defineProperty(el, 'shadowRoot', { value: fakeRoot, configurable: true, writable: true });
  return fakeRoot;
};
`;
export class OgrafRenderer {
    iframe;
    graphicElement;
    strategy;
    captureCanvas;
    captureCtx;
    running = false;
    playbackActive = false;
    rafId;
    onFrame;
    domRenderer = new DomToCanvasRenderer();
    cachedStyles;
    packageId;
    manifest;
    reducedRateTimer;
    constructor(packageId, manifest) {
        this.packageId = packageId;
        this.manifest = manifest;
        this.captureCanvas = new OffscreenCanvas(1920, 1080);
        const ctx = this.captureCanvas.getContext('2d');
        if (!ctx)
            throw new Error('Failed to get 2d context for capture');
        this.captureCtx = ctx;
        this.iframe = document.createElement('iframe');
        this.iframe.style.cssText =
            'position:fixed;left:-9999px;top:-9999px;width:1920px;height:1080px;border:none;visibility:hidden;pointer-events:none;';
        document.body.appendChild(this.iframe);
    }
    async initialize() {
        const iDoc = this.iframe.contentDocument ?? this.iframe.contentWindow?.document;
        if (!iDoc)
            throw new Error('Cannot access iframe document');
        iDoc.open();
        iDoc.write(`<!DOCTYPE html>
<html><head>
<meta charset="UTF-8">
<style>html,body{margin:0;padding:0;width:100%;height:100%;overflow:hidden;background:transparent;}</style>
<script>${SHADOW_DOM_MONKEY_PATCH}</script>
</head><body></body></html>`);
        iDoc.close();
        const mainPath = this.manifest.main;
        const assetUrl = `/ograf-assets/${this.packageId}/${mainPath}`;
        const module = await import(/* webpackIgnore: true */ assetUrl);
        const GraphicClass = module.default || Object.values(module)[0];
        if (!GraphicClass ||
            typeof GraphicClass !== 'function' ||
            !GraphicClass.prototype) {
            throw new Error(`OGraf graphic module does not export a valid class: ${mainPath}`);
        }
        const tagName = `ograf-${this.packageId.replace(/[^a-z0-9-]/gi, '-').toLowerCase()}`;
        const iWin = this.iframe.contentWindow;
        if (!iWin)
            throw new Error('iframe has no contentWindow');
        if (!iWin.customElements.get(tagName)) {
            iWin.customElements.define(tagName, GraphicClass);
        }
        const el = iDoc.createElement(tagName);
        el.style.cssText = 'display:block;width:100%;height:100%;';
        iDoc.body.appendChild(el);
        this.graphicElement = el;
    }
    async load(data) {
        if (!this.graphicElement)
            throw new Error('Not initialized');
        return this.graphicElement.load({
            data,
            renderType: 'realtime',
            renderCharacteristics: {
                resolution: { width: 1920, height: 1080 },
                frameRate: 60,
            },
        });
    }
    async playAction(params) {
        if (!this.graphicElement)
            throw new Error('Not initialized');
        this.playbackActive = true;
        this.startFullRateCapture();
        return this.graphicElement.playAction(params ?? {});
    }
    async stopAction(params) {
        if (!this.graphicElement)
            throw new Error('Not initialized');
        this.playbackActive = false;
        this.stopFullRateCapture();
        this.startReducedRateCapture();
        return this.graphicElement.stopAction(params ?? {});
    }
    async updateAction(params) {
        if (!this.graphicElement)
            throw new Error('Not initialized');
        return this.graphicElement.updateAction(params);
    }
    async customAction(params) {
        if (!this.graphicElement)
            throw new Error('Not initialized');
        return this.graphicElement.customAction(params);
    }
    setFrameCallback(cb) {
        this.onFrame = cb;
    }
    startFullRateCapture() {
        this.stopReducedRateCapture();
        if (this.running)
            return;
        this.running = true;
        this.detectStrategy();
        this.rafId = requestAnimationFrame(() => this.captureLoop());
    }
    stopFullRateCapture() {
        this.running = false;
        if (this.rafId !== undefined) {
            cancelAnimationFrame(this.rafId);
            this.rafId = undefined;
        }
    }
    startReducedRateCapture() {
        this.stopReducedRateCapture();
        this.reducedRateTimer = setInterval(() => {
            this.captureFrame();
        }, 500);
    }
    stopReducedRateCapture() {
        if (this.reducedRateTimer !== undefined) {
            clearInterval(this.reducedRateTimer);
            this.reducedRateTimer = undefined;
        }
    }
    dispose() {
        this.stopFullRateCapture();
        this.stopReducedRateCapture();
        this.onFrame = undefined;
        this.domRenderer.dispose();
        if (this.graphicElement) {
            this.graphicElement.dispose({}).catch(() => { });
        }
        this.iframe.remove();
    }
    detectStrategy() {
        if (this.strategy)
            return;
        try {
            const iDoc = this.iframe.contentDocument;
            if (!iDoc)
                return;
            const innerIframe = iDoc.querySelector('iframe');
            if (innerIframe) {
                try {
                    const innerDoc = innerIframe.contentDocument;
                    if (innerDoc) {
                        const canvas = innerDoc.querySelector('canvas');
                        if (canvas && canvas.width > 10) {
                            this.strategy = {
                                type: 'canvas',
                                source: canvas,
                            };
                            return;
                        }
                    }
                }
                catch {
                    // cross-origin
                }
            }
            const canvases = iDoc.querySelectorAll('canvas');
            for (const canvas of canvases) {
                if (canvas.width > 50) {
                    this.strategy = { type: 'canvas', source: canvas };
                    return;
                }
            }
            this.strategy = { type: 'foreignObject', doc: iDoc };
        }
        catch {
            // iframe not accessible
        }
    }
    captureLoop() {
        if (!this.running)
            return;
        this.detectStrategy();
        this.captureFrame();
        if (this.running) {
            this.rafId = requestAnimationFrame(() => this.captureLoop());
        }
    }
    captureFrame() {
        if (!this.strategy || !this.onFrame)
            return;
        if (this.strategy.type === 'canvas') {
            this.captureFromCanvas(this.strategy.source);
        }
        else {
            this.captureFromForeignObject(this.strategy.doc);
        }
    }
    captureFromCanvas(source) {
        const ctx = this.captureCtx;
        const w = this.captureCanvas.width;
        const h = this.captureCanvas.height;
        ctx.clearRect(0, 0, w, h);
        ctx.drawImage(source, 0, 0, w, h);
        const bitmap = this.captureCanvas.transferToImageBitmap();
        this.onFrame?.(bitmap);
    }
    captureFromForeignObject(doc) {
        const body = doc.body;
        if (!body)
            return;
        if (!this.cachedStyles) {
            this.cachedStyles = this.collectStyles(doc);
        }
        const ctx = this.captureCtx;
        ctx.clearRect(0, 0, this.captureCanvas.width, this.captureCanvas.height);
        this.domRenderer
            .renderToCanvas(body, ctx, undefined, this.cachedStyles)
            .then(() => {
            const bitmap = this.captureCanvas.transferToImageBitmap();
            this.onFrame?.(bitmap);
        })
            .catch(() => { });
    }
    collectStyles(doc) {
        const rules = [];
        try {
            for (let i = 0; i < doc.styleSheets.length; i++) {
                const sheet = doc.styleSheets[i];
                try {
                    for (let j = 0; j < sheet.cssRules.length; j++) {
                        rules.push(sheet.cssRules[j].cssText);
                    }
                }
                catch {
                    // security restriction
                }
            }
        }
        catch {
            // no stylesheets
        }
        return rules.join('\n');
    }
}
